import slugify from "slugify";
import { SellerDto } from "../types/Seller.types";

export const stringToSlug = (str: string) => {
  return slugify(str, { lower: true });
};

export const formatCurrency = (value: string): string => {
  const numericValue = value.replace(/\D/g, "");
  const formattedValue = (Number(numericValue) / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formattedValue;
};

export const maskCurrency = (value: string): string => {
  let numericValue = value.replace(/\D/g, "");

  numericValue = numericValue.replace(/(\d)(\d{8})$/, "$1.$2");
  numericValue = numericValue.replace(/(\d)(\d{5})$/, "$1.$2");
  numericValue = numericValue.replace(/(\d)(\d{2})$/, "$1,$2");

  return `R$ ${numericValue}`;
};

export const maskCurrencyWithoutCents = (value: string): string => {
  if (!value) return "";
  let numericValue = value.replace(/\D/g, "");

  numericValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  return `R$ ${numericValue}`;
};

export const formatCurrencyWithoutCents = (value: number): string => {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const calculateBuyingPower = (
  downPayment: number,
  installment: number
): number => {
  const monthlyInterestRate = 2.0 / 100; // 2.00% ao mês
  const termMonths = 60; // Prazo de 60 meses

  const financingCapacity =
    (installment * (1 - Math.pow(1 + monthlyInterestRate, -termMonths))) /
    monthlyInterestRate;

  return downPayment + financingCapacity;
};

export function maskMileage(value: string): string {
  if (!value) return "";

  let v = value.replace(/\D/g, "");
  v = v.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return v;
}

export function maskPhone(value: string): string {
  if (!value) return "";

  let v = value.replace(/\D/g, "");
  v = v.replace(/^(\d{2})(\d)/g, "($1) $2");
  v = v.replace(/(\d)(\d{4})$/, "$1-$2");
  return v;
}

export function maskCEP(value: string): string {
  if (!value) return "";

  let v = value.replace(/\D/g, "");
  v = v.replace(/^(\d{5})(\d)/, "$1-$2");
  return v;
}

export function maskCNPJ(value: string): string {
  if (!value) return "";

  let v = value.replace(/\D/g, "");
  v = v.replace(/^(\d{2})(\d)/, "$1.$2");
  v = v.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
  v = v.replace(/\.(\d{3})(\d)/, ".$1/$2");
  v = v.replace(/(\d{4})(\d)/, "$1-$2");
  return v;
}

export const removeNonNumericAndSpaces = (inputString: string) => {
  if (typeof inputString !== "string") return "";

  const cleanedString = inputString?.replace(/[^\d]/g, "").replace(/\s/g, "");

  return cleanedString;
};

type BadgeOption = {
  name: string;
  url: string;
};

type BadgeUtilParams<T> = {
  hits: T[];
  getKey: (hit: T) => string | undefined;
  getUrl: (hit: T) => string;
};

export const generateBadgeOptions = <T>({
  hits,
  getKey,
  getUrl,
}: BadgeUtilParams<T>): BadgeOption[] => {
  const options: BadgeOption[] = [];
  const uniqueKeys: Record<string, boolean> = {};

  for (const hit of hits) {
    const key = getKey(hit);
    if (key && !uniqueKeys[key]) {
      uniqueKeys[key] = true;
      options.push({
        name: key,
        url: getUrl(hit),
      });
    }
  }

  return options;
};

export const normalizeString = (str: string): string => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/-/g, " ");
};

export const resetSyncCdnImageSize = ({
  images,
  width,
  height,
  format,
}: {
  images: string[];
  width: number;
  height: number;
  format?: string;
}): string[] => {
  let resizedImages: string[] = [];
  const cdnBaseUrl = "https://cdn.click.autos/";

  if (images.some((image) => image.includes("https"))) {
    resizedImages = images.map(
      (image) =>
        `${image.replace("200x200", `${width}x${height}${format ? `/filters:format(${format})/` : ""}`)}`
    );

    return resizedImages;
  }

  resizedImages = images.map(
    (image) =>
      `${cdnBaseUrl}${width}x${height}${format ? `/filters:format(${format})/` : "/"}${image}`
  );

  return resizedImages;
};

export const normalizeSellerData = (seller: SellerDto) => {
  const normalizedAddresses = seller?.addresses?.map((address) => ({
    ...address,
    latitude: address.latitude ? Number(address.latitude) : null,
    longitude: address.longitude ? Number(address.longitude) : null,
  }));

  return {
    ...seller,
    addresses: normalizedAddresses,
  };
};
